import api from '../httpClient';
import {
  Client,
  ClientsResponse,
  CreateClientPayload,
  ClientResponse,
  type ClientActiveSubscription,
  type SaveClientDraftPayload,
  type ClientDraftListItem,
  type ClientDraftDetail,
} from '@/types/client';
import { CalculationResult } from '@/types/calculations';
import { 
  DietaryGoals, 
  UpdateDietaryGoalsRequest, 
  UpdateDietaryGoalsResponse,
} from '@/types/dietary-goals';
import { CalculationFormula, DRVGuideline } from '@/types/calculation-formulas';
import {
  scheduleNutritionTargetUpdateToast,
  type NutritionTargetUpdateNotice,
} from '@/utils/toast-utils';

const CLIENT_BY_ID_CACHE_TTL_MS = 10_000;
const clientByIdInFlight = new Map<string, Promise<ClientResponse>>();
const clientByIdCache = new Map<string, { timestamp: number; data: ClientResponse }>();
const CLIENT_LAB_REPORT_DETAIL_CACHE_TTL_MS = 5 * 60_000;
const clientLabReportDetailInFlight = new Map<string, Promise<ClientLabReportDetailResponse>>();
const clientLabReportDetailCache = new Map<string, { timestamp: number; data: ClientLabReportDetailResponse }>();
const CLIENT_LAB_REPORT_COMPARE_CACHE_TTL_MS = 5 * 60_000;
const clientLabReportCompareInFlight = new Map<string, Promise<ClientLabReportCompareResponse>>();
const clientLabReportCompareCache = new Map<string, { timestamp: number; data: ClientLabReportCompareResponse }>();

export const LAB_REPORT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const LAB_REPORT_MAX_FILENAME_LENGTH = 255;
export const LAB_REPORT_EXTRACTION_POLL_INTERVAL_MS = 2_000;
export const LAB_REPORT_EXTRACTION_POLL_TIMEOUT_MS = 180_000;
export const LAB_REPORT_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
] as const;

type LabReportAllowedMimeType = (typeof LAB_REPORT_ALLOWED_MIME_TYPES)[number];

export interface ClientLabReportStatusCounts {
  low: number;
  normal: number;
  borderline: number;
  high: number;
  critical: number;
}

export interface ClientLabReportPreviewBiomarker {
  id: string;
  biomarkerId: string;
  biomarkerName: string;
  value: number | null;
  unit: string | null;
  status: string | null;
  testedDate: string | null;
  notes?: string | null;
}

export interface ClientLabReportSummary {
  id: string;
  clientId: string;
  reportName: string;
  labName: string | null;
  testDate: string;
  fileType: 'pdf' | 'image';
  fileCount: number;
  uploadedAt: string;
  notes?: string | null;
  biomarkerCount?: number;
  biomarkerPreview?: ClientLabReportPreviewBiomarker[];
  biomarkerPreviewRemainingCount?: number;
  statusCounts?: ClientLabReportStatusCounts;
}

export interface ClientLabReportStoredBiomarker {
  biomarkerId: string;
  biomarkerName: string;
  value: number;
  unit: string;
  status: string | null;
  rangeSource?: 'report' | 'internal' | 'none' | null;
  rangeLowerBound?: number | null;
  rangeUpperBound?: number | null;
  referenceRangeText?: string | null;
  interpretation?: string | null;
  testedDate: string;
  labReportId: string;
}

export interface ClientLabReportExtractionUsage {
  model: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  totalTokens: number | null;
  promptCostUsd: number | null;
  completionCostUsd: number | null;
  totalCostUsd: number | null;
}

export interface ClientLabReportExtractResponse {
  message: string;
  labReport: ClientLabReportSummary;
  extraction: {
    patient: {
      fullName: string | null;
      ageYears: number | null;
      dateOfBirth: string | null;
      sex: string | null;
      patientId: string | null;
    } | null;
    report: {
      reportId: string | null;
      reportDate: string | null;
      labName: string | null;
      doctorName: string | null;
    } | null;
    extractionNotes: string[];
  };
  usage?: ClientLabReportExtractionUsage | null;
  storage: {
    extractedBiomarkers: number;
    storedBiomarkers: number;
    skippedBiomarkers: Array<{ name: string; reason: string }>;
    partialExtraction?: {
      pageCount: number;
      skippedPages: number[];
    } | null;
    skippedPages?: number[];
    biomarkers: ClientLabReportStoredBiomarker[];
  };
  nutritionTargetUpdate?: NutritionTargetUpdateNotice | null;
}

export type ClientLabReportExtractionState = 'queued' | 'in_progress' | 'completed' | 'failed';
export type ClientLabReportExtractionProgressState = 'uploading' | ClientLabReportExtractionState;

interface ClientLabReportExtractionQueueResponse {
  success?: boolean;
  data?: {
    jobId?: string;
    clientId?: string;
    extractionState?: ClientLabReportExtractionState;
  };
  message?: string;
}

interface ClientLabReportExtractionStatusPayload {
  jobId: string;
  clientId: string;
  extractionState: ClientLabReportExtractionState;
  extractionError: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
  result: ClientLabReportExtractResponse | null;
}

interface ClientLabReportExtractionStatusResponse {
  success?: boolean;
  data?: ClientLabReportExtractionStatusPayload;
}

function expectedExtensionByMimeType(mimeType: LabReportAllowedMimeType): string[] {
  switch (mimeType) {
    case 'application/pdf':
      return ['pdf'];
    case 'image/png':
      return ['png'];
    case 'image/webp':
      return ['webp'];
    case 'image/jpeg':
    case 'image/jpg':
    default:
      return ['jpg', 'jpeg'];
  }
}

export interface ClientLabReportUploadUrlResponse {
  method: 'PUT';
  uploadUrl: string;
  expiresInSeconds: number;
  reportName: string;
  mimeType: string;
  s3Key: string;
  fileUrl: string;
  requiredHeaders: Record<string, string>;
}

export const LAB_REPORT_MAX_FILES = 6;
export const LAB_REPORT_MAX_COMBINED_SIZE_BYTES = 30 * 1024 * 1024;

export interface ClientLabReportMultiUploadEntry {
  index: number;
  method: string;
  uploadUrl: string;
  reportName: string;
  mimeType: string;
  s3Key: string;
  fileUrl: string;
  requiredHeaders: Record<string, string>;
}

export interface ClientLabReportMultiUploadUrlResponse {
  method: string;
  expiresInSeconds: number;
  fileCount: number;
  uploads: ClientLabReportMultiUploadEntry[];
}

export interface GetClientLabReportsParams {
  page?: number;
  limit?: number;
  search?: string;
  fileType?: 'pdf' | 'image';
  biomarkerPreviewCount?: number;
}

export interface ClientLabReportsListResponse {
  reports: ClientLabReportSummary[];
  latestReport: ClientLabReportSummary | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    search: string;
    fileType: 'pdf' | 'image' | '';
  };
}

export interface ClientLabReportBiomarkerPoint {
  value: number | null;
  /** When the API sends `value` as a string, the original text (for exact display). */
  valueText?: string | null;
  unit: string | null;
  status: string | null;
  testedDate: string | null;
  reportId?: string | null;
  rangeSource?: 'report' | 'internal' | 'none' | null;
  rangeLowerBound?: number | null;
  rangeUpperBound?: number | null;
  referenceRangeText?: string | null;
  referenceGuideline?: string | null;
  interpretation?: string | null;
}

export interface ClientLabReportBiomarkerChange {
  absolute: number | null;
  percent: number | null;
  direction: string | null;
}

export interface ClientLabReportPanelBiomarker {
  id: string;
  biomarkerId: string;
  biomarkerName: string;
  category: string | null;
  current: ClientLabReportBiomarkerPoint;
  previous: ClientLabReportBiomarkerPoint | null;
  change: ClientLabReportBiomarkerChange | null;
  trendPoints: ClientLabReportBiomarkerPoint[];
}

export interface ClientLabReportCategorySummary {
  category: string;
  count: number;
}

export interface ClientLabReportDetailResponse {
  report: ClientLabReportSummary | null;
  categories: ClientLabReportCategorySummary[];
  biomarkers: ClientLabReportPanelBiomarker[];
}

export interface GetClientLabReportDownloadUrlParams {
  disposition?: 'inline' | 'attachment';
  fileIndex?: number;
}

export interface ClientLabReportDownloadUrlResponse {
  reportId: string;
  clientId: string;
  disposition: 'inline' | 'attachment';
  fileIndex: number;
  fileCount: number;
  expiresInSeconds: number;
  downloadUrl: string;
}

export interface UpdateClientLabReportPayload {
  reportName?: string;
  labName?: string | null;
  testDate?: string;
  notes?: string | null;
}

export interface UpdateClientLabReportResponse {
  message: string;
  labReport: ClientLabReportSummary;
}

export interface UpdateClientLabReportNotesPayload {
  reportId?: string;
  applyToAll?: boolean;
  notes: string | null;
}

export interface UpdateClientLabReportNotesResponse {
  message: string;
  labReport: ClientLabReportSummary | null;
  updatedCount: number;
}

export interface CompareClientLabReportsParams {
  leftReportId: string;
  rightReportId: string;
}

export interface ClientLabReportCompareBiomarkerSide {
  value: number | null;
  valueText?: string | null;
  unit: string | null;
  status: string | null;
  testedDate: string | null;
  referenceRangeText?: string | null;
}

export interface ClientLabReportCompareBiomarkerDelta {
  absolute: number | null;
  percent: number | null;
  direction: string | null;
}

export interface ClientLabReportCompareBiomarker {
  biomarkerId: string;
  biomarkerName: string;
  category: string | null;
  left: ClientLabReportCompareBiomarkerSide | null;
  right: ClientLabReportCompareBiomarkerSide | null;
  delta: ClientLabReportCompareBiomarkerDelta | null;
}

export interface ClientLabReportCompareSummary {
  comparedBiomarkers: number;
  presentInBothReports: number;
}

export interface ClientLabReportCompareResponse {
  leftReport: ClientLabReportSummary | null;
  rightReport: ClientLabReportSummary | null;
  summary: ClientLabReportCompareSummary;
  biomarkers: ClientLabReportCompareBiomarker[];
}

export interface ClientLabReportAllBiomarkersResponse {
  mode: 'all_reports';
  reports: ClientLabReportSummary[];
  summary: {
    reportCount: number;
    comparedBiomarkers: number;
    presentInMultipleReports: number;
    singleReportOnly: number;
  };
  biomarkers: ClientLabReportPanelBiomarker[];
}

export interface DeleteClientLabReportResponse {
  message: string;
  deleted: {
    reportId: string;
    biomarkerRows: number;
    s3ObjectDeleted: boolean;
  };
  nutritionTargetUpdate?: NutritionTargetUpdateNotice | null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isClientLabReportExtractResponse(value: unknown): value is ClientLabReportExtractResponse {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Record<string, unknown>;
  return Boolean(payload.labReport && payload.storage);
}

function asObjectRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function asNullableString(value: unknown): string | null {
  if (value == null) return null;
  return asNonEmptyString(value);
}

function readApiDataPayload(value: unknown): Record<string, unknown> {
  const root = asObjectRecord(value);
  if (!root) return {};
  const nested = asObjectRecord(root.data);
  return nested || root;
}

function readHttpStatusCode(error: unknown): number | null {
  const root = asObjectRecord(error);
  if (!root) return null;
  const response = asObjectRecord(root.response);
  if (!response) return null;
  const status = coerceFiniteNumber(response.status);
  if (status === null) return null;
  return Math.round(status);
}

function toNonNegativeInt(value: unknown, fallback = 0): number {
  const numeric = coerceFiniteNumber(value);
  if (numeric === null) return fallback;
  return Math.max(0, Math.round(numeric));
}

function normalizeClientLabReportStatusCounts(value: unknown): ClientLabReportStatusCounts {
  const data = asObjectRecord(value) || {};
  return {
    low: toNonNegativeInt(data.low),
    normal: toNonNegativeInt(data.normal),
    borderline: toNonNegativeInt(data.borderline),
    high: toNonNegativeInt(data.high),
    critical: toNonNegativeInt(data.critical),
  };
}

function normalizeClientLabReportSummary(
  value: unknown,
  fallbackClientId: string
): ClientLabReportSummary | null {
  const data = asObjectRecord(value);
  if (!data) return null;

  const id = asNonEmptyString(data.id) || asNonEmptyString(data._id);
  if (!id) return null;

  const reportName = asNonEmptyString(data.reportName)
    || asNonEmptyString(data.fileName)
    || asNonEmptyString(data.name)
    || 'Lab Report';

  const testDate = asNonEmptyString(data.testDate)
    || asNonEmptyString(data.reportDate)
    || asNonEmptyString(data.testedDate)
    || '';

  const fileTypeRaw = asNonEmptyString(data.fileType)?.toLowerCase();
  const inferredFromName = reportName.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
  const fileType: 'pdf' | 'image' =
    fileTypeRaw === 'pdf' ? 'pdf'
      : fileTypeRaw === 'image' ? 'image'
        : inferredFromName;

  const uploadedAt = asNonEmptyString(data.uploadedAt)
    || asNonEmptyString(data.createdAt)
    || asNonEmptyString(data.addedAt)
    || testDate
    || new Date().toISOString();

  const biomarkerCount = toNonNegativeInt(data.biomarkerCount);
  const biomarkerPreviewRemainingCount = toNonNegativeInt(data.biomarkerPreviewRemainingCount);
  const biomarkerPreviewRaw = Array.isArray(data.biomarkerPreview) ? data.biomarkerPreview : [];
  const biomarkerPreview = biomarkerPreviewRaw
    .map((item, index) => {
      const preview = asObjectRecord(item);
      if (!preview) return null;
      const biomarkerId = asNonEmptyString(preview.biomarkerId);
      if (!biomarkerId) return null;
      const previewId = asNonEmptyString(preview.id)
        || asNonEmptyString(preview._id)
        || `${id}::preview::${biomarkerId}::${index}`;
      return {
        id: previewId,
        biomarkerId,
        biomarkerName: asNonEmptyString(preview.biomarkerName) || biomarkerId,
        value: coerceFiniteNumber(preview.value),
        unit: asNullableString(preview.unit),
        status: asNullableString(preview.status),
        testedDate: asNullableString(preview.testedDate),
        notes: asNullableString(preview.notes),
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  return {
    id,
    clientId: asNonEmptyString(data.clientId) || fallbackClientId,
    reportName,
    labName: asNullableString(data.labName),
    testDate,
    fileType,
    uploadedAt,
    notes: asNullableString(data.notes),
    biomarkerCount,
    biomarkerPreview,
    biomarkerPreviewRemainingCount,
    fileCount: Math.max(1, toNonNegativeInt(data.fileCount) || 1),
    statusCounts: normalizeClientLabReportStatusCounts(data.statusCounts),
  };
}

function normalizeClientLabReportBiomarkerPoint(value: unknown): ClientLabReportBiomarkerPoint {
  const data = asObjectRecord(value) || {};
  const rangeSource = asNullableString(data.rangeSource);
  const referenceGuideline = asNonEmptyString(data.referenceGuideline)
    || asNonEmptyString(data.referenceGuidelineName)
    || asNonEmptyString(data.guideline)
    || asNonEmptyString(data.guidelineName)
    || asNonEmptyString(data.rangeGuideline)
    || asNonEmptyString(data.rangeGuidelineName)
    || null;
  const rawVal = data.value;
  let pointValue: number | null;
  let valueText: string | null = null;
  if (typeof rawVal === 'string') {
    const trimmed = rawVal.trim();
    if (trimmed) {
      valueText = trimmed;
      pointValue = coerceFiniteNumber(trimmed);
    } else {
      pointValue = null;
    }
  } else {
    pointValue = coerceFiniteNumber(rawVal);
  }
  return {
    value: pointValue,
    valueText,
    unit: asNullableString(data.unit),
    status: asNullableString(data.status),
    testedDate: asNullableString(data.testedDate),
    reportId: asNullableString(data.reportId),
    rangeSource:
      rangeSource === 'report' || rangeSource === 'internal' || rangeSource === 'none'
        ? rangeSource
        : null,
    rangeLowerBound: coerceFiniteNumber(data.rangeLowerBound),
    rangeUpperBound: coerceFiniteNumber(data.rangeUpperBound),
    referenceRangeText: asNullableString(data.referenceRangeText),
    referenceGuideline,
    interpretation: asNullableString(data.interpretation),
  };
}

function normalizeClientLabReportPanelBiomarker(value: unknown): ClientLabReportPanelBiomarker | null {
  const data = asObjectRecord(value);
  if (!data) return null;

  const biomarkerId = asNonEmptyString(data.biomarkerId);
  if (!biomarkerId) return null;
  const currentRaw = asObjectRecord(data.current) || {};
  const current = normalizeClientLabReportBiomarkerPoint({
    ...data,
    ...currentRaw,
    rangeSource: currentRaw.rangeSource ?? data.rangeSource,
    rangeLowerBound: currentRaw.rangeLowerBound ?? data.rangeLowerBound,
    rangeUpperBound: currentRaw.rangeUpperBound ?? data.rangeUpperBound,
    referenceRangeText: currentRaw.referenceRangeText ?? data.referenceRangeText,
    referenceGuideline: currentRaw.referenceGuideline ?? data.referenceGuideline,
    interpretation: currentRaw.interpretation ?? data.interpretation,
    value: currentRaw.value ?? data.value,
    unit: currentRaw.unit ?? data.unit,
    status: currentRaw.status ?? data.status,
    testedDate: currentRaw.testedDate ?? data.testedDate,
    reportId: currentRaw.reportId ?? data.reportId,
  });
  const currentReportId = current.reportId || asNonEmptyString(asObjectRecord(data.current)?.reportId);
  const id = asNonEmptyString(data.id)
    || asNonEmptyString(data._id)
    || `${biomarkerId}::${currentReportId || 'all_reports'}`;

  const trendPoints = Array.isArray(data.trendPoints)
    ? data.trendPoints.map((point) => normalizeClientLabReportBiomarkerPoint(point))
    : [];

  return {
    id,
    biomarkerId,
    biomarkerName: asNonEmptyString(data.biomarkerName) || biomarkerId,
    category: asNullableString(data.category),
    current,
    previous: data.previous ? normalizeClientLabReportBiomarkerPoint(data.previous) : null,
    change: (() => {
      const change = asObjectRecord(data.change);
      if (!change) return null;
      return {
        absolute: coerceFiniteNumber(change.absolute),
        percent: coerceFiniteNumber(change.percent),
        direction: asNullableString(change.direction),
      };
    })(),
    trendPoints,
  };
}

function normalizeClientLabReportCompareSide(value: unknown): ClientLabReportCompareBiomarkerSide | null {
  const data = asObjectRecord(value);
  if (!data) return null;
  const rawVal = data.value;
  let pointValue: number | null;
  let valueText: string | null = null;
  if (typeof rawVal === 'string') {
    const trimmed = rawVal.trim();
    if (trimmed) {
      valueText = trimmed;
      pointValue = coerceFiniteNumber(trimmed);
    } else {
      pointValue = null;
    }
  } else {
    pointValue = coerceFiniteNumber(rawVal);
  }
  return {
    value: pointValue,
    valueText,
    unit: asNullableString(data.unit),
    status: asNullableString(data.status),
    testedDate: asNullableString(data.testedDate),
    referenceRangeText: asNullableString(data.referenceRangeText),
  };
}

function normalizeClientLabReportCompareDelta(value: unknown): ClientLabReportCompareBiomarkerDelta | null {
  const data = asObjectRecord(value);
  if (!data) return null;
  return {
    absolute: coerceFiniteNumber(data.absolute),
    percent: coerceFiniteNumber(data.percent),
    direction: asNullableString(data.direction),
  };
}

function normalizeClientLabReportCompareBiomarker(value: unknown): ClientLabReportCompareBiomarker | null {
  const data = asObjectRecord(value);
  if (!data) return null;
  const biomarkerId = asNonEmptyString(data.biomarkerId);
  if (!biomarkerId) return null;
  return {
    biomarkerId,
    biomarkerName: asNonEmptyString(data.biomarkerName) || biomarkerId,
    category: asNullableString(data.category),
    left: normalizeClientLabReportCompareSide(data.left),
    right: normalizeClientLabReportCompareSide(data.right),
    delta: normalizeClientLabReportCompareDelta(data.delta),
  };
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function pickFirstNumber(candidates: Record<string, unknown>[], keys: string[]): number | null {
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = coerceFiniteNumber(candidate[key]);
      if (value !== null) {
        return value;
      }
    }
  }
  return null;
}

function pickFirstString(candidates: Record<string, unknown>[], keys: string[]): string | null {
  for (const candidate of candidates) {
    for (const key of keys) {
      const value = candidate[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }
  return null;
}

function normalizeLabReportUsageMetrics(payload: Record<string, unknown>): ClientLabReportExtractionUsage | null {
  const extraction = asObjectRecord(payload.extraction);
  const extractionUsage = extraction ? asObjectRecord(extraction.usage) : null;
  const extractionUsageMetadata = extraction ? asObjectRecord(extraction.usageMetadata) : null;
  const usage = asObjectRecord(payload.usage);
  const usageMetadata = asObjectRecord(payload.usageMetadata);
  const metrics = asObjectRecord(payload.metrics);
  const billing = asObjectRecord(payload.billing);

  const candidates = [usage, extractionUsage, usageMetadata, extractionUsageMetadata, metrics, billing, payload].filter(
    (value): value is Record<string, unknown> => Boolean(value)
  );

  const promptTokens = pickFirstNumber(candidates, [
    'promptTokens',
    'inputTokens',
    'promptTokenCount',
    'inputTokenCount',
  ]);
  const completionTokens = pickFirstNumber(candidates, [
    'completionTokens',
    'outputTokens',
    'candidatesTokenCount',
    'outputTokenCount',
  ]);
  const totalTokensCandidate = pickFirstNumber(candidates, ['totalTokens', 'totalTokenCount']);
  const totalTokens =
    totalTokensCandidate
    ?? (
      promptTokens !== null && completionTokens !== null
        ? promptTokens + completionTokens
        : null
    );

  const promptCostUsd = pickFirstNumber(candidates, ['promptCostUsd', 'inputCostUsd']);
  const completionCostUsd = pickFirstNumber(candidates, ['completionCostUsd', 'outputCostUsd']);
  const totalCostCandidate = pickFirstNumber(candidates, [
    'totalCostUsd',
    'costUsd',
    'estimatedCostUsd',
    'estimatedCost',
    'totalCost',
    'cost',
  ]);
  const totalCostUsd =
    totalCostCandidate
    ?? (
      promptCostUsd !== null && completionCostUsd !== null
        ? promptCostUsd + completionCostUsd
        : null
    );

  const model = pickFirstString(candidates, ['model', 'modelName', 'modelId']);

  const hasUsageData =
    model !== null
    || promptTokens !== null
    || completionTokens !== null
    || totalTokens !== null
    || promptCostUsd !== null
    || completionCostUsd !== null
    || totalCostUsd !== null;

  if (!hasUsageData) {
    return null;
  }

  return {
    model,
    promptTokens,
    completionTokens,
    totalTokens,
    promptCostUsd,
    completionCostUsd,
    totalCostUsd,
  };
}

function normalizeClientLabReportExtractResponse(
  response: ClientLabReportExtractResponse
): ClientLabReportExtractResponse {
  const payload = asObjectRecord(response);
  if (!payload) return response;
  const normalized = {
    ...response,
    usage: normalizeLabReportUsageMetrics(payload),
  };
  scheduleNutritionTargetUpdateToast(normalized);
  return normalized;
}

export function validateLabReportFile(file: File | null | undefined): string | null {
  if (!file) {
    return 'Please select a PDF or image file.';
  }
  if (!file.size) {
    return 'Selected file is empty.';
  }
  if (file.size > LAB_REPORT_MAX_FILE_SIZE_BYTES) {
    return 'File is too large. Maximum allowed size is 10 MB.';
  }

  const normalizedMime = String(file.type || '').toLowerCase();
  if (!LAB_REPORT_ALLOWED_MIME_TYPES.includes(normalizedMime as LabReportAllowedMimeType)) {
    return 'Unsupported file type. Allowed: PDF, JPG, PNG, WEBP.';
  }

  const fileName = file.name || '';
  if (!fileName.trim()) {
    return 'Selected file has an invalid name.';
  }
  if (fileName.length > LAB_REPORT_MAX_FILENAME_LENGTH) {
    return `File name is too long. Maximum allowed length is ${LAB_REPORT_MAX_FILENAME_LENGTH} characters.`;
  }

  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const allowedExts = expectedExtensionByMimeType(normalizedMime as LabReportAllowedMimeType);
  if (!ext || !allowedExts.includes(ext)) {
    return `File extension does not match file type (${normalizedMime}).`;
  }

  return null;
}

export function validateLabReportFiles(files: File[]): string | null {
  if (!files.length) {
    return 'Please select at least one PDF or image file.';
  }
  if (files.length > LAB_REPORT_MAX_FILES) {
    return `Too many files. Maximum allowed is ${LAB_REPORT_MAX_FILES} files per report.`;
  }
  for (const file of files) {
    const error = validateLabReportFile(file);
    if (error) return error;
  }
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > LAB_REPORT_MAX_COMBINED_SIZE_BYTES) {
    return 'Total file size is too large. Maximum combined size is 30 MB.';
  }
  return null;
}

// Get clients dashboard list
export const getClientsDashboard = async (params: {
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}) => {
  const query = new URLSearchParams();
  if (params.status) query.append('status', params.status);
  if (params.search) query.append('search', params.search);
  if (params.sortBy) query.append('sortBy', params.sortBy);
  if (params.sortOrder) query.append('sortOrder', params.sortOrder);
  if (params.page) query.append('page', params.page.toString());
  if (params.limit) query.append('limit', params.limit.toString());

  const queryString = query.toString();
  const response = await api.get(`/clients/dashboard${queryString ? `?${queryString}` : ''}`);
  const data = response.data;
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;

  return {
    counts: {
      all: data?.counts?.all ?? 0,
      active: data?.counts?.active ?? 0,
      suspended: data?.counts?.suspended ?? 0,
      drafts: data?.counts?.drafts ?? 0,
      pendingDeletion: data?.counts?.pendingDeletion ?? 0,
    },
    clients: data?.clients ?? [],
    pagination: {
      page: data?.pagination?.page ?? page,
      limit: data?.pagination?.limit ?? limit,
      total: data?.pagination?.total ?? 0,
      pages: data?.pagination?.totalPages ?? data?.pagination?.pages ?? 0,
    },
  };
};

// Save a new onboarding draft (POST /clients/drafts)
export const saveClientDraft = async (payload: SaveClientDraftPayload) => {
  const response = await api.post('/clients/drafts', payload);
  return response.data as { message: string; client: ClientDraftDetail };
};

// Update an existing onboarding draft (PUT /clients/drafts/:id)
export const updateClientDraft = async (draftClientId: string, payload: SaveClientDraftPayload) => {
  const response = await api.put(`/clients/drafts/${encodeURIComponent(draftClientId)}`, payload);
  return response.data as { message: string; client: ClientDraftDetail };
};

/** Create or update a draft depending on whether draftClientId is already known. */
export const saveOrUpdateClientDraft = async (payload: SaveClientDraftPayload) => {
  if (payload.draftClientId) {
    return updateClientDraft(payload.draftClientId, payload);
  }
  return saveClientDraft(payload);
};

// Load a draft and its onboarding snapshot for resume (GET /clients/drafts/:id)
export const getClientDraft = async (draftClientId: string) => {
  const response = await api.get(`/clients/drafts/${encodeURIComponent(draftClientId)}`);
  return response.data as { client: ClientDraftDetail };
};

// List/search draft clients only (GET /clients/drafts) — dedicated data source, never falls back to Active/All Clients.
export const getClientDrafts = async (params: { page?: number; limit?: number; search?: string }) => {
  const query = new URLSearchParams();
  if (params.search) query.append('search', params.search);
  if (params.page) query.append('page', params.page.toString());
  if (params.limit) query.append('limit', params.limit.toString());

  const queryString = query.toString();
  const response = await api.get(`/clients/drafts${queryString ? `?${queryString}` : ''}`);
  const data = response.data;
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;

  return {
    counts: {
      all: data?.counts?.all ?? 0,
      active: data?.counts?.active ?? 0,
      suspended: data?.counts?.suspended ?? 0,
      drafts: data?.counts?.drafts ?? 0,
      pendingDeletion: data?.counts?.pendingDeletion ?? 0,
    },
    clients: (data?.clients ?? []) as ClientDraftListItem[],
    pagination: {
      page: data?.pagination?.page ?? page,
      limit: data?.pagination?.limit ?? limit,
      total: data?.pagination?.total ?? 0,
      pages: data?.pagination?.totalPages ?? data?.pagination?.pages ?? 0,
    },
  };
};

const normalizeClientGoalsShape = (raw: unknown): ClientResponse => {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const envelope =
    root.client && typeof root.client === 'object'
      ? (root as unknown as ClientResponse)
      : root.data && typeof root.data === 'object'
        ? (root.data as ClientResponse)
        : (root as unknown as ClientResponse);

  const client = envelope?.client as (Client & { activeSubscription?: ClientActiveSubscription | null }) | undefined;
  const activeSubscription =
    envelope?.activeSubscription ??
    client?.activeSubscription ??
    null;

  // Normalize `clientGoals` to a consistent shape for the UI.
  // Backend may return some nutrients as `{ value, unit }` while the UI expects `{ grams, kcal }`.
  const goals = client?.clientGoals;
  if (client && goals) {
    const normalizeMacro = (
      macro: {
        value?: number | null;
        unit?: string;
        grams?: { value: number | null; unit: string };
        kcal?: { value: number | null; unit: string };
      } | undefined,
      defaultUnit: string
    ) => {
      if (!macro) return macro;
      if (!macro.grams && (macro.value !== undefined || macro.unit !== undefined)) {
        return {
          ...macro,
          grams: {
            value: macro.value ?? null,
            unit: macro.unit || defaultUnit,
          },
          // Preserve kcal if backend provided it, otherwise keep unset (UI will default to null on save)
          kcal: macro.kcal,
        };
      }
      return macro;
    };

    client.clientGoals = {
      ...goals,
      protein: normalizeMacro(goals.protein as any, 'g') as any,
      totalFat: normalizeMacro(goals.totalFat as any, 'g') as any,
      saturatedFat: normalizeMacro(goals.saturatedFat as any, 'g') as any,
    };
  }

  if (!client) {
    throw new Error('Client response is missing client');
  }

  return {
    client,
    activeSubscription,
  };
};

// Get clients with pagination
export const getClients = async (params: {
  limit?: number;
  page?: number;
  status?: string;
  search?: string;
  appointmentId?: string | number;
  clientId?: string;
  priorityClientId?: string;
} = {}): Promise<ClientsResponse> => {
  const { limit = 10, page = 1, status, search, appointmentId, clientId, priorityClientId } = params;
  const query = new URLSearchParams({
    limit: String(limit),
    page: String(page),
  });

  if (status && status !== 'preferences') {
    query.append('status', status);
  }
  if (search && search.trim()) {
    query.append('search', search.trim());
  }
  if (appointmentId) {
    query.append('appointmentId', String(appointmentId));
  }
  const selectedClientId = clientId?.trim() || priorityClientId?.trim();
  if (selectedClientId) {
    query.append('clientId', selectedClientId);
  }

  const response = await api.get(`/clients?${query.toString()}`);
  
  // Map API response pagination (totalPages) to frontend expected format (pages)
  const data = response.data;
  return {
    clients: data.clients,
    pagination: {
      page: data.pagination?.page || page,
      limit: data.pagination?.limit || limit,
      total: data.pagination?.total || 0,
      pages: data.pagination?.totalPages || data.pagination?.pages || 0
    }
  };
};

// Get client by ID
export const getClientById = async (
  clientId: string,
  options: { force?: boolean } = {}
): Promise<ClientResponse> => {
  const { force = false } = options;
  const cacheKey = clientId;

  if (!force) {
    const cached = clientByIdCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp <= CLIENT_BY_ID_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = clientByIdInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }
  }

  const requestPromise: Promise<ClientResponse> = api
    .get(`/clients/${clientId}`)
    .then((response) => normalizeClientGoalsShape(response.data as ClientResponse))
    .then((data) => {
      clientByIdCache.set(cacheKey, { timestamp: Date.now(), data });
      return data;
    })
    .finally(() => {
      if (clientByIdInFlight.get(cacheKey) === requestPromise) {
        clientByIdInFlight.delete(cacheKey);
      }
    });

  clientByIdInFlight.set(cacheKey, requestPromise);
  return requestPromise;
};

export interface ClientConsentStatus {
  enforced: boolean;
  accessBlocked: boolean;
  reason: 'missing' | 'revoked' | null;
  granted: boolean;
  revoked: boolean;
  policyVersion: string | null;
  platformAcceptedAt: string | null;
  dataProcessingAcceptedAt: string | null;
  grantedAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  invitePending: boolean;
  inviteTokenExpiresAt: string | null;
  lastInviteSentAt: string | null;
  lastInviteError: string | null;
}

export interface ResendClientConsentInviteResponse {
  message: string;
  inviteEmailSent: boolean;
  inviteEmailError: string | null;
  resendAvailableInSeconds?: number;
  consent: ClientConsentStatus;
}

export const resendClientConsentInvite = async (
  clientId: string,
  payload: { inviteNotes?: string | null } = {}
): Promise<ResendClientConsentInviteResponse> => {
  const normalizedClientId = clientId.trim();
  if (!normalizedClientId) {
    throw new Error('Client ID is required to resend consent email.');
  }

  const response = await api.post<ResendClientConsentInviteResponse>(
    `/clients/${encodeURIComponent(normalizedClientId)}/consent/resend`,
    payload
  );
  return response.data;
};

// Create client (unified POST /clients — quick intake via intakeMode: "quick", else flexible profile)
export const createClient = async (clientData: CreateClientPayload) => {
  const response = await api.post('/clients', clientData);
  scheduleNutritionTargetUpdateToast(response.data);
  return response.data;
};

export const updateClient = async (clientId: string, nutritionalData: any) => {
  const response = await api.put(`/clients/${clientId}`, nutritionalData);
  scheduleNutritionTargetUpdateToast(response.data);
  return response.data;
};

export const invalidateClientByIdCache = (clientId?: string) => {
  if (clientId) {
    clientByIdCache.delete(clientId);
    clientByIdInFlight.delete(clientId);
    return;
  }
  clientByIdCache.clear();
  clientByIdInFlight.clear();
};

export const deleteClient = async (clientId: string): Promise<{ message: string }> => {
  const normalizedClientId = clientId.trim();
  if (!normalizedClientId) {
    throw new Error('Client ID is required to delete a client.');
  }

  const response = await api.delete(`/clients/${encodeURIComponent(normalizedClientId)}`);
  invalidateClientByIdCache(normalizedClientId);
  const data = response.data as { message?: string } | undefined;
  return {
    message: data?.message || 'Client deleted successfully',
  };
};

export const deleteClientDraft = async (
  draftClientId: string
): Promise<{ message: string; deleted: boolean; clientId: string }> => {
  const normalizedDraftClientId = draftClientId.trim();
  if (!normalizedDraftClientId) {
    throw new Error('Draft client ID is required to delete a draft.');
  }

  const response = await api.delete(`/clients/drafts/${encodeURIComponent(normalizedDraftClientId)}`);
  const data = response.data as { message?: string; deleted?: boolean; clientId?: string } | undefined;
  return {
    message: data?.message || 'Draft deleted permanently',
    deleted: data?.deleted === true,
    clientId: data?.clientId || normalizedDraftClientId,
  };
};

export const extractClientLabReport = async (
  clientId: string,
  file: File,
  options: {
    reportName?: string;
    reportDate?: string | null;
    labName?: string | null;
    notes?: string | null;
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
    onProgress?: (state: ClientLabReportExtractionProgressState) => void;
  } = {}
): Promise<ClientLabReportExtractResponse> => {
  const validationError = validateLabReportFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const mimeType = file.type.toLowerCase() as LabReportAllowedMimeType;
  const pollIntervalMs = Number.isFinite(options.pollIntervalMs)
    ? Math.max(500, Math.floor(options.pollIntervalMs as number))
    : LAB_REPORT_EXTRACTION_POLL_INTERVAL_MS;
  const pollTimeoutMs = Number.isFinite(options.pollTimeoutMs)
    ? Math.max(5_000, Math.floor(options.pollTimeoutMs as number))
    : LAB_REPORT_EXTRACTION_POLL_TIMEOUT_MS;

  options.onProgress?.('uploading');

  const uploadUrlResponse = await api.post(
    `/clients/${clientId}/lab-reports/upload-url`,
    {
      fileName: file.name,
      mimeType,
      fileSize: file.size,
    }
  );
  const uploadConfig = uploadUrlResponse.data as ClientLabReportUploadUrlResponse;

  const uploadResponse = await fetch(uploadConfig.uploadUrl, {
    method: uploadConfig.method || 'PUT',
    headers: uploadConfig.requiredHeaders || {
      'Content-Type': mimeType,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const uploadErrorBody = await uploadResponse.text().catch(() => '');
    throw new Error(
      uploadErrorBody
        ? `Failed to upload lab report (${uploadResponse.status}): ${uploadErrorBody}`
        : `Failed to upload lab report (${uploadResponse.status}).`
    );
  }

  const payload = {
    s3Key: uploadConfig.s3Key,
    mimeType,
    fileName: file.name,
    reportName: options.reportName || uploadConfig.reportName || file.name,
    reportDate: options.reportDate ?? null,
    labName: options.labName ?? null,
    notes: options.notes ?? null,
  };

  const response = await api.post(`/clients/${clientId}/lab-reports/extract`, payload);
  const responseData = response.data as ClientLabReportExtractResponse | ClientLabReportExtractionQueueResponse;

  if (isClientLabReportExtractResponse(responseData)) {
    options.onProgress?.('completed');
    return normalizeClientLabReportExtractResponse(responseData);
  }

  const queuedJobId = responseData?.data?.jobId;
  const initialState = responseData?.data?.extractionState;
  if (
    typeof queuedJobId !== 'string'
    || (initialState !== 'queued' && initialState !== 'in_progress' && initialState !== 'completed' && initialState !== 'failed')
  ) {
    throw new Error('Unexpected response from lab report extraction queue endpoint.');
  }

  options.onProgress?.(initialState);

  const deadline = Date.now() + pollTimeoutMs;
  let isFirstPoll = true;
  while (Date.now() < deadline) {
    if (!isFirstPoll) {
      await sleep(pollIntervalMs);
    }
    isFirstPoll = false;

    const statusResponse = await api.get(`/clients/${clientId}/lab-reports/extract/${queuedJobId}`);
    const statusBody = statusResponse.data as ClientLabReportExtractionStatusResponse | ClientLabReportExtractionStatusPayload;
    const statusPayload = (
      statusBody && typeof statusBody === 'object' && 'data' in statusBody
        ? statusBody.data
        : statusBody
    ) as ClientLabReportExtractionStatusPayload | undefined;

    const state = statusPayload?.extractionState;
    if (!state || (state !== 'queued' && state !== 'in_progress' && state !== 'completed' && state !== 'failed')) {
      throw new Error('Unexpected response from lab report extraction status endpoint.');
    }

    options.onProgress?.(state);

    if (state === 'completed') {
      if (!isClientLabReportExtractResponse(statusPayload.result)) {
        throw new Error('Lab report extraction completed but result payload is missing.');
      }
      return normalizeClientLabReportExtractResponse(statusPayload.result);
    }

    if (state === 'failed') {
      throw new Error(statusPayload.extractionError || 'Lab report extraction failed.');
    }
  }

  throw new Error('Lab report extraction is taking longer than expected. Please refresh after a moment.');
};

export const extractClientLabReportMulti = async (
  clientId: string,
  files: File[],
  options: {
    reportName?: string;
    reportDate?: string | null;
    labName?: string | null;
    notes?: string | null;
    pollIntervalMs?: number;
    pollTimeoutMs?: number;
    onProgress?: (state: ClientLabReportExtractionProgressState) => void;
  } = {}
): Promise<ClientLabReportExtractResponse> => {
  const validationError = validateLabReportFiles(files);
  if (validationError) {
    throw new Error(validationError);
  }

  const pollIntervalMs = Number.isFinite(options.pollIntervalMs)
    ? Math.max(500, Math.floor(options.pollIntervalMs as number))
    : LAB_REPORT_EXTRACTION_POLL_INTERVAL_MS;
  const pollTimeoutMs = Number.isFinite(options.pollTimeoutMs)
    ? Math.max(5_000, Math.floor(options.pollTimeoutMs as number))
    : LAB_REPORT_EXTRACTION_POLL_TIMEOUT_MS;

  options.onProgress?.('uploading');

  const uploadUrlResponse = await api.post(
    `/clients/${clientId}/lab-reports/upload-url`,
    {
      files: files.map((file) => ({
        fileName: file.name,
        mimeType: file.type.toLowerCase(),
        fileSize: file.size,
      })),
    }
  );
  const uploadConfigRaw = uploadUrlResponse.data as ClientLabReportMultiUploadUrlResponse | ClientLabReportUploadUrlResponse;
  const uploadConfig: ClientLabReportMultiUploadUrlResponse = Array.isArray((uploadConfigRaw as ClientLabReportMultiUploadUrlResponse).uploads)
    ? (uploadConfigRaw as ClientLabReportMultiUploadUrlResponse)
    : {
      method: (uploadConfigRaw as ClientLabReportUploadUrlResponse).method,
      expiresInSeconds: (uploadConfigRaw as ClientLabReportUploadUrlResponse).expiresInSeconds,
      fileCount: 1,
      uploads: [{
        index: 0,
        method: (uploadConfigRaw as ClientLabReportUploadUrlResponse).method,
        uploadUrl: (uploadConfigRaw as ClientLabReportUploadUrlResponse).uploadUrl,
        reportName: (uploadConfigRaw as ClientLabReportUploadUrlResponse).reportName,
        mimeType: (uploadConfigRaw as ClientLabReportUploadUrlResponse).mimeType,
        s3Key: (uploadConfigRaw as ClientLabReportUploadUrlResponse).s3Key,
        fileUrl: (uploadConfigRaw as ClientLabReportUploadUrlResponse).fileUrl,
        requiredHeaders: (uploadConfigRaw as ClientLabReportUploadUrlResponse).requiredHeaders,
      }],
    };

  await Promise.all(
    uploadConfig.uploads.map((entry) => {
      const file = files[entry.index];
      return fetch(entry.uploadUrl, {
        method: entry.method || 'PUT',
        headers: entry.requiredHeaders || { 'Content-Type': file.type.toLowerCase() },
        body: file,
      }).then(async (uploadResponse) => {
        if (!uploadResponse.ok) {
          const body = await uploadResponse.text().catch(() => '');
          throw new Error(
            body
              ? `Failed to upload file "${file.name}" (${uploadResponse.status}): ${body}`
              : `Failed to upload file "${file.name}" (${uploadResponse.status}).`
          );
        }
      });
    })
  );

  const reportName = options.reportName || files[0]?.name || 'lab-report';
  const payload = {
    files: uploadConfig.uploads.map((entry) => ({
      s3Key: entry.s3Key,
      mimeType: files[entry.index].type.toLowerCase(),
      fileName: files[entry.index].name,
    })),
    reportName,
    reportDate: options.reportDate ?? null,
    labName: options.labName ?? null,
    notes: options.notes ?? null,
  };

  const response = await api.post(`/clients/${clientId}/lab-reports/extract`, payload);
  const responseData = response.data as ClientLabReportExtractResponse | ClientLabReportExtractionQueueResponse;

  if (isClientLabReportExtractResponse(responseData)) {
    options.onProgress?.('completed');
    return normalizeClientLabReportExtractResponse(responseData);
  }

  const queuedJobId = responseData?.data?.jobId;
  const initialState = responseData?.data?.extractionState;
  if (
    typeof queuedJobId !== 'string'
    || (initialState !== 'queued' && initialState !== 'in_progress' && initialState !== 'completed' && initialState !== 'failed')
  ) {
    throw new Error('Unexpected response from lab report extraction queue endpoint.');
  }

  options.onProgress?.(initialState);

  const deadline = Date.now() + pollTimeoutMs;
  let isFirstPoll = true;
  while (Date.now() < deadline) {
    if (!isFirstPoll) {
      await sleep(pollIntervalMs);
    }
    isFirstPoll = false;

    const statusResponse = await api.get(`/clients/${clientId}/lab-reports/extract/${queuedJobId}`);
    const statusBody = statusResponse.data as ClientLabReportExtractionStatusResponse | ClientLabReportExtractionStatusPayload;
    const statusPayload = (
      statusBody && typeof statusBody === 'object' && 'data' in statusBody
        ? statusBody.data
        : statusBody
    ) as ClientLabReportExtractionStatusPayload | undefined;

    const state = statusPayload?.extractionState;
    if (!state || (state !== 'queued' && state !== 'in_progress' && state !== 'completed' && state !== 'failed')) {
      throw new Error('Unexpected response from lab report extraction status endpoint.');
    }

    options.onProgress?.(state);

    if (state === 'completed') {
      if (!isClientLabReportExtractResponse(statusPayload.result)) {
        throw new Error('Lab report extraction completed but result payload is missing.');
      }
      return normalizeClientLabReportExtractResponse(statusPayload.result);
    }

    if (state === 'failed') {
      throw new Error(statusPayload.extractionError || 'Lab report extraction failed.');
    }
  }

  throw new Error('Lab report extraction is taking longer than expected. Please refresh after a moment.');
};

function buildClientLabReportDetailCacheKey(clientId: string, reportId: string): string {
  return `${clientId}::${reportId}`;
}

function buildClientLabReportCompareCacheKey(
  clientId: string,
  leftReportId: string,
  rightReportId: string
): string {
  return `${clientId}::${leftReportId}::${rightReportId}`;
}

function clearClientLabReportDetailCache(clientId: string, reportId: string): void {
  const cacheKey = buildClientLabReportDetailCacheKey(clientId, reportId);
  clientLabReportDetailCache.delete(cacheKey);
  clientLabReportDetailInFlight.delete(cacheKey);
}

function clearClientLabReportCompareCache(clientId: string, reportId: string): void {
  const prefix = `${clientId}::`;
  for (const cacheKey of clientLabReportCompareCache.keys()) {
    if (!cacheKey.startsWith(prefix)) continue;
    const [, leftReportId, rightReportId] = cacheKey.split('::');
    if (leftReportId === reportId || rightReportId === reportId) {
      clientLabReportCompareCache.delete(cacheKey);
      clientLabReportCompareInFlight.delete(cacheKey);
    }
  }
}

function clearClientLabReportCachesForClient(clientId: string): void {
  const prefix = `${clientId}::`;
  for (const cacheKey of clientLabReportDetailCache.keys()) {
    if (cacheKey.startsWith(prefix)) {
      clientLabReportDetailCache.delete(cacheKey);
      clientLabReportDetailInFlight.delete(cacheKey);
    }
  }
  for (const cacheKey of clientLabReportCompareCache.keys()) {
    if (cacheKey.startsWith(prefix)) {
      clientLabReportCompareCache.delete(cacheKey);
      clientLabReportCompareInFlight.delete(cacheKey);
    }
  }
}

export const getClientLabReports = async (
  clientId: string,
  params: GetClientLabReportsParams = {}
): Promise<ClientLabReportsListResponse> => {
  const resolvedPage = Math.max(1, toNonNegativeInt(params.page, 1));
  const resolvedLimit = Math.max(1, toNonNegativeInt(params.limit, 20));
  const resolvedBiomarkerPreviewCount = Math.max(0, toNonNegativeInt(params.biomarkerPreviewCount, 6));
  const search = params.search?.trim();

  const detailsV2Query = new URLSearchParams();
  detailsV2Query.append('fromPage', String(resolvedPage));
  detailsV2Query.append('fromLimit', String(resolvedLimit));
  if (resolvedBiomarkerPreviewCount > 0) {
    detailsV2Query.append('biomarkerPreviewCount', String(resolvedBiomarkerPreviewCount));
  }
  if (search) detailsV2Query.append('search', search);
  if (params.fileType) detailsV2Query.append('fileType', params.fileType);

  const legacyQuery = new URLSearchParams();
  legacyQuery.append('page', String(resolvedPage));
  legacyQuery.append('limit', String(resolvedLimit));
  if (resolvedBiomarkerPreviewCount > 0) {
    legacyQuery.append('biomarkerPreviewCount', String(resolvedBiomarkerPreviewCount));
  }
  if (search) legacyQuery.append('search', search);
  if (params.fileType) legacyQuery.append('fileType', params.fileType);

  const detailsV2Suffix = detailsV2Query.toString() ? `?${detailsV2Query.toString()}` : '';
  const legacySuffix = legacyQuery.toString() ? `?${legacyQuery.toString()}` : '';

  let response;
  try {
    // Keep legacy endpoint as primary to avoid regressions in environments
    // where details-v2 is not deployed yet.
    response = await api.get(`/clients/${clientId}/lab-reports${legacySuffix}`);
  } catch (error) {
    const statusCode = readHttpStatusCode(error);
    if (statusCode !== 400 && statusCode !== 404 && statusCode !== 405) {
      throw error;
    }
    response = await api.get(`/clients/${clientId}/details-v2/lab-reports${detailsV2Suffix}`);
  }
  const payload = readApiDataPayload(response.data);

  const reports = Array.isArray(payload.reports)
    ? payload.reports
      .map((item) => normalizeClientLabReportSummary(item, clientId))
      .filter((item): item is ClientLabReportSummary => Boolean(item))
    : [];

  const latestReport = normalizeClientLabReportSummary(payload.latestReport, clientId);
  const pagination = asObjectRecord(payload.pagination) || {};
  const filters = asObjectRecord(payload.filters) || {};
  const normalizedFileType = asNonEmptyString(filters.fileType)?.toLowerCase();

  return {
    reports,
    latestReport,
    pagination: {
      page: Math.max(1, toNonNegativeInt(
        pagination.page,
        toNonNegativeInt(pagination.fromPage, resolvedPage)
      )),
      limit: Math.max(1, toNonNegativeInt(
        pagination.limit,
        toNonNegativeInt(pagination.fromLimit, resolvedLimit)
      )),
      total: toNonNegativeInt(
        pagination.total,
        toNonNegativeInt(pagination.fromTotal, reports.length)
      ),
      totalPages: Math.max(1, toNonNegativeInt(
        pagination.totalPages,
        toNonNegativeInt(pagination.fromTotalPages, 1)
      )),
    },
    filters: {
      search: asNullableString(filters.search) || '',
      fileType:
        normalizedFileType === 'pdf' || normalizedFileType === 'image'
          ? normalizedFileType
          : '',
    },
  };
};

export const getClientLabReport = async (
  clientId: string,
  reportId: string,
  options: { force?: boolean } = {}
): Promise<ClientLabReportDetailResponse> => {
  const { force = false } = options;
  const cacheKey = buildClientLabReportDetailCacheKey(clientId, reportId);

  if (!force) {
    const cached = clientLabReportDetailCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp <= CLIENT_LAB_REPORT_DETAIL_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = clientLabReportDetailInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }
  }

  const requestPromise: Promise<ClientLabReportDetailResponse> = api
    .get(`/clients/${clientId}/lab-reports/${reportId}`)
    .then((response) => {
      const payload = readApiDataPayload(response.data);
      const report = normalizeClientLabReportSummary(payload.report, clientId);
      const categoriesRaw = Array.isArray(payload.categories) ? payload.categories : [];
      const biomarkersRaw = Array.isArray(payload.biomarkers) ? payload.biomarkers : [];

      return {
        report,
        categories: categoriesRaw.map((item) => {
          const category = asObjectRecord(item) || {};
          return {
            category: asNonEmptyString(category.category) || 'uncategorized',
            count: toNonNegativeInt(category.count),
          };
        }),
        biomarkers: biomarkersRaw
          .map((item) => normalizeClientLabReportPanelBiomarker(item))
          .filter((item): item is ClientLabReportPanelBiomarker => Boolean(item)),
      };
    })
    .then((data) => {
      clientLabReportDetailCache.set(cacheKey, { timestamp: Date.now(), data });
      return data;
    })
    .finally(() => {
      if (clientLabReportDetailInFlight.get(cacheKey) === requestPromise) {
        clientLabReportDetailInFlight.delete(cacheKey);
      }
    });

  clientLabReportDetailInFlight.set(cacheKey, requestPromise);
  return requestPromise;
};

export const getClientLabReportDownloadUrl = async (
  clientId: string,
  reportId: string,
  params: GetClientLabReportDownloadUrlParams = {}
): Promise<ClientLabReportDownloadUrlResponse> => {
  const queryParams: Record<string, string | number> = {};
  if (params.disposition) queryParams.disposition = params.disposition;
  if (typeof params.fileIndex === 'number') queryParams.fileIndex = params.fileIndex;

  const response = await api.get(`/clients/${clientId}/lab-reports/${reportId}/download-url`, {
    params: Object.keys(queryParams).length ? queryParams : undefined,
  });
  const payload = readApiDataPayload(response.data);

  const dispositionRaw = asNonEmptyString(payload.disposition)?.toLowerCase();
  const disposition: 'inline' | 'attachment' = dispositionRaw === 'attachment' ? 'attachment' : 'inline';
  const downloadUrl = asNullableString(payload.downloadUrl);
  if (!downloadUrl) {
    throw new Error('Download URL is missing in lab report response.');
  }

  return {
    reportId: asNonEmptyString(payload.reportId) || reportId,
    clientId: asNonEmptyString(payload.clientId) || clientId,
    disposition,
    fileIndex: toNonNegativeInt(payload.fileIndex),
    fileCount: Math.max(1, toNonNegativeInt(payload.fileCount) || 1),
    expiresInSeconds: toNonNegativeInt(payload.expiresInSeconds),
    downloadUrl,
  };
};

export const updateClientLabReport = async (
  clientId: string,
  reportId: string,
  payload: UpdateClientLabReportPayload
): Promise<UpdateClientLabReportResponse> => {
  const response = await api.patch(`/clients/${clientId}/lab-reports/${reportId}`, payload);
  const body = readApiDataPayload(response.data);
  const labReport = normalizeClientLabReportSummary(body.labReport, clientId);
  if (!labReport) {
    throw new Error('Unexpected response while updating lab report.');
  }

  clearClientLabReportDetailCache(clientId, reportId);
  clearClientLabReportCompareCache(clientId, reportId);

  return {
    message: asNonEmptyString(body.message) || 'Lab report updated successfully',
    labReport,
  };
};

export const updateClientLabReportNotes = async (
  clientId: string,
  payload: UpdateClientLabReportNotesPayload
): Promise<UpdateClientLabReportNotesResponse> => {
  const shouldApplyToAll = payload.applyToAll === true;
  const reportId = asNonEmptyString(payload.reportId);

  if (!shouldApplyToAll && !reportId) {
    throw new Error('reportId is required when applyToAll is not true.');
  }

  const requestBody: Record<string, unknown> = {
    notes: payload.notes ?? null,
  };
  if (shouldApplyToAll) {
    requestBody.applyToAll = true;
  } else {
    requestBody.reportId = reportId;
  }

  const response = await api.patch(`/clients/${clientId}/lab-reports/notes`, requestBody);
  const body = readApiDataPayload(response.data);
  const normalizedReport = normalizeClientLabReportSummary(body.labReport, clientId);

  if (shouldApplyToAll) {
    clearClientLabReportCachesForClient(clientId);
  } else if (reportId) {
    clearClientLabReportDetailCache(clientId, reportId);
    clearClientLabReportCompareCache(clientId, reportId);
  } else if (normalizedReport?.id) {
    clearClientLabReportDetailCache(clientId, normalizedReport.id);
    clearClientLabReportCompareCache(clientId, normalizedReport.id);
  }

  return {
    message: asNonEmptyString(body.message) || 'Lab report notes updated successfully',
    labReport: normalizedReport,
    updatedCount: toNonNegativeInt(body.updatedCount),
  };
};

export const compareClientLabReports = async (
  clientId: string,
  params: CompareClientLabReportsParams,
  options: { force?: boolean } = {}
): Promise<ClientLabReportCompareResponse> => {
  const { force = false } = options;
  const cacheKey = buildClientLabReportCompareCacheKey(clientId, params.leftReportId, params.rightReportId);

  if (!force) {
    const cached = clientLabReportCompareCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp <= CLIENT_LAB_REPORT_COMPARE_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = clientLabReportCompareInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }
  }

  const requestPromise: Promise<ClientLabReportCompareResponse> = api
    .get(`/clients/${clientId}/lab-reports/compare`, {
      params: {
        leftReportId: params.leftReportId,
        rightReportId: params.rightReportId,
      },
    })
    .then((response) => {
      const payload = readApiDataPayload(response.data);

      const leftReport = normalizeClientLabReportSummary(payload.leftReport, clientId);
      const rightReport = normalizeClientLabReportSummary(payload.rightReport, clientId);
      const summary = asObjectRecord(payload.summary) || {};
      const biomarkersRaw = Array.isArray(payload.biomarkers) ? payload.biomarkers : [];

      return {
        leftReport,
        rightReport,
        summary: {
          comparedBiomarkers: toNonNegativeInt(summary.comparedBiomarkers),
          presentInBothReports: toNonNegativeInt(summary.presentInBothReports),
        },
        biomarkers: biomarkersRaw
          .map((item) => normalizeClientLabReportCompareBiomarker(item))
          .filter((item): item is ClientLabReportCompareBiomarker => Boolean(item)),
      };
    })
    .then((data) => {
      clientLabReportCompareCache.set(cacheKey, { timestamp: Date.now(), data });
      return data;
    })
    .finally(() => {
      if (clientLabReportCompareInFlight.get(cacheKey) === requestPromise) {
        clientLabReportCompareInFlight.delete(cacheKey);
      }
    });

  clientLabReportCompareInFlight.set(cacheKey, requestPromise);
  return requestPromise;
};

export const getClientLabReportsAllBiomarkers = async (
  clientId: string
): Promise<ClientLabReportAllBiomarkersResponse> => {
  const response = await api.get(`/clients/${clientId}/lab-reports/compare`, {
    params: { mode: 'all_reports' },
  });
  const payload = readApiDataPayload(response.data);
  const reportsRaw = Array.isArray(payload.reports) ? payload.reports : [];
  const summaryRaw = asObjectRecord(payload.summary) || {};
  const biomarkersRaw = Array.isArray(payload.biomarkers) ? payload.biomarkers : [];

  const reports = reportsRaw
    .map((item) => normalizeClientLabReportSummary(item, clientId))
    .filter((item): item is ClientLabReportSummary => Boolean(item));

  const biomarkers = biomarkersRaw
    .map((item) => normalizeClientLabReportPanelBiomarker(item))
    .filter((item): item is ClientLabReportPanelBiomarker => Boolean(item));

  return {
    mode: 'all_reports',
    reports,
    summary: {
      reportCount: toNonNegativeInt(summaryRaw.reportCount),
      comparedBiomarkers: toNonNegativeInt(summaryRaw.comparedBiomarkers),
      presentInMultipleReports: toNonNegativeInt(summaryRaw.presentInMultipleReports),
      singleReportOnly: toNonNegativeInt(summaryRaw.singleReportOnly),
    },
    biomarkers,
  };
};

export const deleteClientLabReport = async (
  clientId: string,
  reportId: string
): Promise<DeleteClientLabReportResponse> => {
  const response = await api.delete(`/clients/${clientId}/lab-reports/${reportId}`);
  const payload = readApiDataPayload(response.data);
  const deleted = asObjectRecord(payload.deleted) || {};

  clearClientLabReportDetailCache(clientId, reportId);
  clearClientLabReportCompareCache(clientId, reportId);

  const result = {
    message: asNonEmptyString(payload.message) || 'Lab report deleted successfully',
    deleted: {
      reportId: asNonEmptyString(deleted.reportId) || reportId,
      biomarkerRows: toNonNegativeInt(deleted.biomarkerRows),
      s3ObjectDeleted: deleted.s3ObjectDeleted === true,
    },
    nutritionTargetUpdate: (payload.nutritionTargetUpdate ?? null) as NutritionTargetUpdateNotice | null,
  };
  scheduleNutritionTargetUpdateToast(result);
  return result;
};

export interface CreateClientCalculationPreviewResponse {
  message: string;
  preview: CalculationResult;
}

export interface CheckClientPhoneParams {
  phone: string;
  phoneCountryCode: string;
  excludeClientId?: string;
}

export interface CheckClientEmailParams {
  email: string;
  excludeClientId?: string;
}

export interface CheckClientPhoneResponse {
  phone: string;
  phoneCountryCode: string;
  countryCode?: string;
  location?: string;
  isTaken?: boolean;
  isUnique?: boolean;
  exists?: boolean;
  raw: unknown;
}

export interface CheckClientEmailResponse {
  email: string;
  isTaken?: boolean;
  isUnique?: boolean;
  exists?: boolean;
  raw: unknown;
}

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const firstBoolean = (...values: unknown[]): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') return value;
  }
  return undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

export const checkClientPhone = async (
  params: CheckClientPhoneParams
): Promise<CheckClientPhoneResponse> => {
  const response = await api.get('/clients/check-phone', { params });
  const root = asRecord(response.data) ?? {};
  const payload = asRecord(root.data) ?? root;

  const phone = firstNonEmptyString(
    payload.phone,
    payload.normalizedPhone,
    payload.phoneNumber,
    params.phone
  ) || params.phone;

  const phoneCountryCode = firstNonEmptyString(
    payload.phoneCountryCode,
    payload.phone_country_code,
    root.phoneCountryCode,
    root.phone_country_code,
    params.phoneCountryCode
  ) || params.phoneCountryCode;

  const countryCode = firstNonEmptyString(
    payload.countryCode,
    payload.country_code,
    payload.isoCountryCode,
    payload.phoneCountry,
    payload.phone_country
  )?.toLowerCase();

  const location = firstNonEmptyString(
    payload.location,
    payload.country,
    payload.countryName
  );

  const isTaken = firstBoolean(payload.isTaken, root.isTaken);
  const isUnique = firstBoolean(payload.isUnique, root.isUnique);

  const existsCandidate = payload.exists ?? payload.isExistingClient ?? payload.isRegistered;
  const exists = typeof existsCandidate === 'boolean'
    ? existsCandidate
    : isTaken === true
      ? true
      : isUnique === true
        ? false
        : undefined;

  return {
    phone,
    phoneCountryCode,
    countryCode,
    location,
    isTaken,
    isUnique,
    exists,
    raw: response.data,
  };
};

export const checkClientEmail = async (
  params: CheckClientEmailParams
): Promise<CheckClientEmailResponse> => {
  const response = await api.get('/clients/check-email', { params });
  const root = asRecord(response.data) ?? {};
  const payload = asRecord(root.data) ?? root;

  const email = firstNonEmptyString(
    payload.email,
    payload.normalizedEmail,
    root.email,
    params.email
  ) || params.email;

  const isTaken = firstBoolean(payload.isTaken, root.isTaken);
  const isUnique = firstBoolean(payload.isUnique, root.isUnique);

  const existsCandidate = payload.exists ?? payload.isExistingClient ?? payload.isRegistered;
  const exists = typeof existsCandidate === 'boolean'
    ? existsCandidate
    : isTaken === true
      ? true
      : isUnique === true
        ? false
        : undefined;

  return {
    email,
    isTaken,
    isUnique,
    exists,
    raw: response.data,
  };
};

// Calculate client values without creating/saving a client
export const calculateClientPreview = async (
  clientData: CreateClientPayload,
  signal?: AbortSignal
): Promise<CreateClientCalculationPreviewResponse> => {
  const response = await api.post('/clients/calculate', clientData, { signal });
  return response.data;
};

// Calculate EER, BMR, BMI, and other values
export const calculationNutritionalTargets = async (data: any) => {
  const response = await api.post('/calculations', {
    ...data,
  });
  return response.data;
};

// Get calculation formulas
export const getCalculationFormulas = async (): Promise<{ formulas: CalculationFormula[] }> => {
  const response = await api.get('/calculations/formulas');
  // Backend returns { formulas: [...] } directly
  return response.data;
};

// Response type for viable formulas API
export interface ViableFormulasResponse {
  defaultFormulaId: string;
  allFormulas: CalculationFormula[];
  viableFormulas: CalculationFormula[];
  defaultDRVGuidelineId?: string;
  availableDRVGuidelines?: DRVGuideline[];
  allowedActivityLevels?: {
    ageGroup: string;
    options: Array<{
      value: string;
      label: string;
      description: string;
      palValue: number;
      isDefault: boolean;
      iomPaMale?: number;
      iomPaFemale?: number;
    }>;
  };
}

// Get viable formulas for age, gender, and country
export const getViableFormulas = async (params: {
  age: number;
  gender: string;
  countryCode?: string;
}): Promise<ViableFormulasResponse> => {
  const queryParams = new URLSearchParams({
    age: params.age.toString(),
    gender: params.gender,
  });
  if (params.countryCode) {
    queryParams.append('countryCode', params.countryCode);
  }
  const response = await api.get(`/calculations/viable-formulas?${queryParams.toString()}`);
  return response.data;
};

// Update dietary goals for a client
export const updateDietaryGoals = async (
  clientId: string,
  dietaryGoals: DietaryGoals
): Promise<UpdateDietaryGoalsResponse> => {
  const response = await api.post('/meal-plans', {
    type: 'client-goal',
    clientId,
    ...dietaryGoals
  });
  return response.data;
};

// Get meal plans for a specific client
export const getClientMealPlans = async (clientId: string, page: number = 1, pageSize: number = 10): Promise<any> => {
  const response = await api.get(`/meal-plans?clientId=${clientId}&page=${page}&pageSize=${pageSize}`);
  return response.data;
};

// Get recent meal plans for a specific client
// Always uses page=1 and pageSize=3 as per API requirements
export const getRecentMealPlans = async (clientId: string): Promise<any> => {
  const response = await api.get(`/meal-plans/recent?clientId=${clientId}&page=1&pageSize=3`);
  return response.data;
};

// Get client notes
export const getClientNotes = async (clientId: string) => {
  const response = await api.get(`/clients/${clientId}/notes`);
  return response.data;
};

// Add client note
export const addClientNote = async (clientId: string, payload: { title: string; content: string; flag: string }) => {
  const response = await api.post(`/clients/${clientId}/notes`, payload);
  return response.data;
};

// Get client medical conditions
export const getClientMedicalConditions = async (clientId: string) => {
  const response = await api.get(`/clients/${clientId}/medical-conditions`);
  return response.data;
};

export interface ClientOverviewAppointment {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  timezone?: string;
  status: string;
  appointmentType?: string;
  meetingLink?: string;
  location?: string;
}

export interface ClientOverviewQuestionnaireItem {
  instanceId: string;
  questionnaireId: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | string;
  numberOfQuestions: number;
  assignedAt: string;
  completedAt: string | null;
}

export interface ClientOverviewMedicalCondition {
  id: string;
  conditionId: string;
  name: string;
  displayName?: string;
  autoimmuneSummary?: string;
  comorbidities?: any;
  status: string;
  diagnosisDate: string | null;
}

export interface ClientOverviewResponse {
  clientId: string;
  nextAppointment: ClientOverviewAppointment | null;
  questionnaires: {
    summary: {
      totalShared: number;
      completed: number;
      inProgress: number;
    };
    items: ClientOverviewQuestionnaireItem[];
  };
  goals: {
    clientGoals: {
      protein?: {
        grams?: { value: number; unit: string };
        kcal?: { value: number; unit: string };
      };
      carbohydrates?: {
        energyPercent?: { value: number; unit: string };
        kcal?: { value: number; unit: string };
        grams?: { value: number; unit: string };
      };
      dietaryFibre?: { value: number; unit: string };
      totalFat?: {
        grams?: { value: number; unit: string };
        kcal?: { value: number; unit: string };
      };
      saturatedFat?: {
        grams?: { value: number; unit: string };
        kcal?: { value: number; unit: string };
      };
      water?: { value: number; unit: string };
      [key: string]: any;
    } | null;
    weightGoal: string | null;
    healthGoals: string[];
  };
  medicalConditions: ClientOverviewMedicalCondition[];
}

// Get client overview data (appointments, questionnaires, goals, medical conditions)
export const getClientOverview = async (clientId: string): Promise<ClientOverviewResponse> => {
  const response = await api.get(`/clients/${clientId}/overview`);
  return response.data;
};

export interface ClientBasicDetailsResponse {
  client: {
    id: string;
    fullName: string;
    email: string;
    location: string | null;
    age: number | null;
    gender: string | null;
    heightCm: number | null;
    weightKg: number | null;
    bmi: number | null;
    bmr: number | null;
    bodyFatPercentage: number | null;
  };
  mealPlan: {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
  } | null;
  appointments: {
    lastSession: {
      id: string;
      type: 'online' | 'offline' | null;
      startTime: string | null;
      endTime: string | null;
      notes: string | null;
    } | null;
    nextSession: {
      id: string;
      type: 'online' | 'offline' | null;
      startTime: string | null;
      endTime: string | null;
      notes: string | null;
    } | null;
  };
  dietary: {
    dietaryType?: string | null;
    dietaryTags: string[];
    allergies: string[];
    foodPreferences: string[];
    medicalConditions?: string[] | string | null;
    foodRestrictions?: string[] | string | null;
  };
  supplements: Array<{
    id: string | null;
    supplementName: string;
    dose: string;
    frequency: string;
    reason: string | null;
    startedAt: string | null;
  }>;
  clientNotes: Array<{
    id: string | null;
    title: string | null;
    content: string;
    flag: 'urgent' | 'warning' | 'note';
    createdAt: string;
    authorName?: string | null;
    createdBy?: string | null;
  }>;
  questionnaires?: {
    totalShared: number;
    completed: number;
    completedPercentage: number;
  };
}

export const getClientBasicDetails = async (clientId: string): Promise<ClientBasicDetailsResponse> => {
  const response = await api.get(`/clients/${clientId}/basic-details`);
  return response.data;
};
